import { useState, useEffect, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  createZProject,
  updateZProject,
  createZCreation,
  logZGeneration,
  generateZImage,
  startZVideo,
  pollZVideo,
  persistZMedia,
  zAdCopy,
  zHashtags,
  zSlogans,
  zCampaign,
  zSketchPrompt,
  zGarmentPrompt,
  listZProducts,
  listZServices,
  firstProductImage,
  Z_KINDS,
  Z_STYLES,
  Z_ENVIRONMENTS,
  Z_POSES,
  Z_MODEL_TYPES,
  Z_OBJECTIVES,
  Z_SEASONS,
  Z_PLATFORMS,
  Z_ASPECTS,
  type ZProductItem,
  type ZServiceItem,
  type ZFactsheet,
} from '@/lib/zstudio';

type SourceKey = 'idea' | 'product' | 'service' | 'sketch' | 'garment';

const SOURCES: { key: SourceKey; label: string; desc: string; icon: string }[] = [
  { key: 'idea', label: 'Décrire une idée', desc: 'Vous décrivez ce que vous voulez', icon: 'ri-quill-pen-line' },
  { key: 'product', label: 'Produit Zifek', desc: 'Utiliser un produit existant', icon: 'ri-shopping-bag-3-line' },
  { key: 'service', label: 'Service Zifek', desc: 'Utiliser un service existant', icon: 'ri-service-line' },
  { key: 'sketch', label: 'Croquis', desc: 'Croquis → photo de mode', icon: 'ri-pencil-ruler-2-line' },
  { key: 'garment', label: 'Photo du vêtement', desc: 'Vêtement réel → mannequin', icon: 'ri-t-shirt-line' },
];

const STEP_LABELS = ['Type', 'Source', 'Détails', 'Style', 'Format'];

// Presets de démarrage rapide (spec 67) — configurent le wizard en un clic.
const PRESETS: { key: string; label: string; desc: string; icon: string }[] = [
  { key: 'product-pro', label: 'Photo produit pro', desc: 'Fond neutre, éclairage studio', icon: 'ri-camera-lens-line' },
  { key: 'before-after', label: 'Before / After', desc: 'Transformation avant / après', icon: 'ri-contrast-2-line' },
  { key: 'reel-vertical', label: 'Reel vertical', desc: 'Vidéo courte 9:16', icon: 'ri-smartphone-line' },
];
const DELAY = (ms: number) => new Promise((r) => setTimeout(r, ms));

function Toggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${active ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
    >
      {children}
    </button>
  );
}

export default function ZStudioWizardPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<string>('image');
  const [source, setSource] = useState<SourceKey>('idea');
  const [subject, setSubject] = useState('');
  const [brief, setBrief] = useState('');
  const [products, setProducts] = useState<ZProductItem[]>([]);
  const [services, setServices] = useState<ZServiceItem[]>([]);
  const [productId, setProductId] = useState<number | null>(null);
  const [serviceId, setServiceId] = useState<number | null>(null);
  const [style, setStyle] = useState(Z_STYLES[0]);
  const [environment, setEnvironment] = useState(Z_ENVIRONMENTS[0]);
  const [pose, setPose] = useState(Z_POSES[0]);
  const [modelType, setModelType] = useState(Z_MODEL_TYPES[0]);
  const [objective, setObjective] = useState(Z_OBJECTIVES[0]);
  const [season, setSeason] = useState(Z_SEASONS[0]);
  const [aspect, setAspect] = useState('9:16');
  const [platforms, setPlatforms] = useState<string[]>(['Instagram']);
  const [aspect3, setAspect3] = useState({ image: true, video: false, text: true, hashtags: true, slogan: false });

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [doneCount, setDoneCount] = useState(0);
  const [projectId, setProjectId] = useState<number | null>(null);

  const applyPreset = (key: string) => {
    setError(null);
    setActivePreset((prev) => (prev === key ? null : key));
    if (key === 'product-pro') {
      setKind('image'); setSource('product'); setAspect('1:1');
      setAspect3({ image: true, video: false, text: false, hashtags: false, slogan: false });
    } else if (key === 'reel-vertical') {
      setKind('video'); setAspect('9:16');
      setAspect3({ image: true, video: true, text: false, hashtags: false, slogan: false });
    } else if (key === 'before-after') {
      setKind('image'); setAspect('1:1');
      setAspect3({ image: true, video: false, text: false, hashtags: false, slogan: false });
    }
  };

  useEffect(() => {
    const t = params.get('type');
    const m = params.get('mode');
    const preset = params.get('preset');
    const subj = params.get('subject');
    const brf = params.get('brief');
    if (t) setKind(t);
    if (subj) setSubject(subj);
    if (brf) setBrief(brf);
    if (m === 'sketch') { setKind('fashion'); setSource('sketch'); }
    else if (m === 'video') setKind('video');
    else if (m === 'ad') setKind('ad');
    else if (m === 'social') setKind('social');
    else if (m === 'product') { setKind('product'); setSource('product'); }
    else if (m === 'service') { setKind('service'); setSource('service'); }
    else if (m === 'campaign') setKind('campaign');
    if (preset) applyPreset(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!user?.id) return;
    if (source === 'product' && products.length === 0) listZProducts().then(setProducts).catch(() => {});
    if (source === 'service' && services.length === 0) listZServices().then(setServices).catch(() => {});
  }, [source, user?.id, products.length, services.length]);

  const isFashion = source === 'sketch' || source === 'garment' || kind === 'fashion';

  const factsheet = (): ZFactsheet | undefined => {
    if (source === 'product' && productId) {
      const p = products.find((x) => x.id === productId);
      if (p) return { name: p.name, kind: 'Produit', price: p.price != null ? `${p.price} ${p.currency || ''}`.trim() : undefined, description: p.description || undefined };
    }
    if (source === 'service' && serviceId) {
      const s = services.find((x) => x.id === serviceId);
      if (s) return { name: s.name, kind: 'Service', category: s.category || undefined, price: s.price != null ? String(s.price) : undefined, description: s.description || undefined };
    }
    return undefined;
  };

  const resolveSubject = (): string => {
    if (source === 'product' && productId) return products.find((x) => x.id === productId)?.name || subject;
    if (source === 'service' && serviceId) return services.find((x) => x.id === serviceId)?.name || subject;
    return subject;
  };

  const buildVisualPrompt = async (subjectName: string): Promise<string> => {
    if (source === 'sketch') {
      const r = await zSketchPrompt({ description: brief || subjectName, modelType, style: style.toLowerCase(), environment, pose, language: 'fr' });
      return r.image_prompt || `${subjectName}. ${brief}. Style ${style}, ${environment}.`;
    }
    if (source === 'garment') {
      const r = await zGarmentPrompt({ description: brief || subjectName, modelType, style: style.toLowerCase(), environment, pose, language: 'fr' });
      return r.image_prompt || `${subjectName}. ${brief}. Style ${style}, ${environment}.`;
    }
    const f = factsheet();
    const presetNote = activePreset === 'before-after'
      ? 'Composition comparative avant / après, transformation visuelle nette et réaliste, même sujet conservé.'
      : activePreset === 'product-pro'
        ? 'Mise en scène produit professionnelle, fond studio neutre, éclairage soigné.'
        : '';
    const parts = [
      subjectName,
      f?.description || brief,
      presetNote,
      `Style : ${style}. Environnement : ${environment}.`,
      isFashion ? `Mannequin : ${modelType}, pose ${pose}.` : '',
      `Photo professionnelle, éclairage soigné, haute qualité.`,
    ].filter(Boolean);
    return parts.join(' ');
  };

  const run = async () => {
    if (!user?.id || running) return;
    const subjectName = resolveSubject().trim() || (isFashion ? 'Vêtement' : 'Création');
    if (!subjectName && source === 'idea' && !brief.trim()) {
      setError('Décrivez ce que vous voulez créer.');
      return;
    }
    setRunning(true);
    setError(null);
    setDoneCount(0);
    setProgress('Préparation du projet…');
    try {
      const project = await createZProject({
        ownerId: user.id,
        name: subjectName.slice(0, 120),
        kind,
        style,
        aspectRatio: aspect,
        sourceType: source,
        sourceRef: productId ? String(productId) : serviceId ? String(serviceId) : undefined,
        brief: brief || undefined,
        branding: {},
        meta: { objective, season, platforms },
      });
      setProjectId(project.id);
      const f = factsheet();
      let created = 0;

      // 1) Campagne complète : on génère d'abord le concept + storyboard + prompts.
      let campaignPrompts: { image?: string; video?: string } = {};
      if (kind === 'campaign') {
        setProgress('Élaboration du concept de campagne…');
        const cmp = await zCampaign({ subject: subjectName, factsheet: f, objective, platforms, season, style, language: 'fr' });
        campaignPrompts = { image: cmp.image_prompt, video: cmp.video_prompt };
        const summary = [
          cmp.concept ? `Concept : ${cmp.concept}` : '',
          cmp.slogan ? `Slogan : ${cmp.slogan}` : '',
          cmp.cta ? `CTA : ${cmp.cta}` : '',
          cmp.caption ? `\n${cmp.caption}` : '',
        ].filter(Boolean).join('\n');
        await createZCreation({
          ownerId: user.id, projectId: project.id, kind: 'campaign', mediaKind: 'text',
          url: summary, title: `Campagne — ${subjectName}`,
          content: { concept: cmp.concept, slogan: cmp.slogan, caption: cmp.caption, hashtags: cmp.hashtags, cta: cmp.cta, storyboard: cmp.storyboard },
          provider: cmp.provider,
        });
        await logZGeneration({ ownerId: user.id, projectId: project.id, type: 'campaign', prompt: subjectName, model: cmp.provider, provider: cmp.provider, output: { concept: cmp.concept }, status: 'done' });
        created += 1;
        setDoneCount(created);
      }

      // 2) Textes (caption, hashtags, slogan).
      if (aspect3.text || kind === 'ad' || kind === 'social') {
        setProgress('Rédaction du texte publicitaire…');
        const copy = await zAdCopy({ subject: subjectName, factsheet: f, objective, platforms, language: 'fr' });
        await createZCreation({
          ownerId: user.id, projectId: project.id, kind: kind === 'ad' ? 'ad' : 'social', mediaKind: 'text',
          url: copy.long_text || copy.caption, title: `Texte — ${subjectName}`,
          content: { caption: copy.caption, short_text: copy.short_text, long_text: copy.long_text, call_to_action: copy.call_to_action, per_platform: copy.per_platform },
          provider: copy.provider,
        });
        await logZGeneration({ ownerId: user.id, projectId: project.id, type: 'ad_copy', prompt: subjectName, provider: copy.provider, output: { caption: copy.caption }, status: 'done' });
        created += 1;
        setDoneCount(created);
      }
      if (aspect3.hashtags) {
        setProgress('Génération des hashtags…');
        const tags = await zHashtags({ subject: subjectName, factsheet: f, count: 15, language: 'fr' });
        await createZCreation({
          ownerId: user.id, projectId: project.id, kind: 'social', mediaKind: 'text',
          url: tags.join(' '), title: `Hashtags — ${subjectName}`, content: { hashtags: tags },
        });
        await logZGeneration({ ownerId: user.id, projectId: project.id, type: 'hashtags', prompt: subjectName, output: { tags }, status: 'done' });
        created += 1;
        setDoneCount(created);
      }
      if (aspect3.slogan) {
        setProgress('Proposition de slogans…');
        const slogans = await zSlogans({ subject: subjectName, factsheet: f, count: 5, language: 'fr' });
        await createZCreation({
          ownerId: user.id, projectId: project.id, kind: 'ad', mediaKind: 'text',
          url: slogans.join('\n'), title: `Slogans — ${subjectName}`, content: { slogans },
        });
        created += 1;
        setDoneCount(created);
      }

      // 3) Image.
      let imageUrl: string | null = null;
      if ((aspect3.image || kind === 'image') && kind !== 'video') {
        setProgress('Génération de l’image (IA)…');
        const basePrompt = campaignPrompts.image || await buildVisualPrompt(subjectName);
        const urls = await generateZImage({ prompt: basePrompt, n: 1, aspectRatio: aspect, persist: true, folder: 'zstudio/images' });
        imageUrl = urls[0] || null;
        if (imageUrl) {
          await createZCreation({
            ownerId: user.id, projectId: project.id, kind: isFashion ? 'fashion' : 'image', mediaKind: 'image',
            url: imageUrl, thumbnail_url: imageUrl, title: `${isFashion ? 'Mode' : 'Image'} — ${subjectName}`,
            content: { prompt: basePrompt, aspect },
          });
          await logZGeneration({ ownerId: user.id, projectId: project.id, type: 'image', prompt: basePrompt, provider: 'xAI', output: { url: imageUrl }, status: 'done' });
          created += 1;
          setDoneCount(created);
        }
      }

      // 4) Vidéo (asynchrone : on suit l'avancement).
      if (aspect3.video || kind === 'video') {
        setProgress('Lancement de la vidéo (IA)…');
        const basePrompt = campaignPrompts.video || await buildVisualPrompt(subjectName);
        const { requestId } = await startZVideo({ prompt: basePrompt, duration: 5, aspectRatio: aspect, image: imageUrl || undefined });
        if (requestId) {
          let videoUrl: string | null = null;
          for (let i = 0; i < 45; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            await DELAY(8000);
            // eslint-disable-next-line no-await-in-loop
            const st = await pollZVideo(requestId);
            const s = (st.status || '').toLowerCase();
            if (st.videoUrl && (s.includes('complet') || s.includes('succeed') || s.includes('ready') || s.includes('done'))) { videoUrl = st.videoUrl; break; }
            if (s.includes('fail') || s.includes('error')) throw new Error(st.error || 'Échec de la génération vidéo.');
            setProgress(`Génération de la vidéo… (${Math.min(95, (i + 1) * 4)}%)`);
          }
          if (videoUrl) {
            setProgress('Finalisation du lien vidéo…');
            const permanent = await persistZMedia(videoUrl, `zstudio-video-${Date.now()}.mp4`, 'zstudio/videos', 'video/mp4');
            await createZCreation({
              ownerId: user.id, projectId: project.id, kind: isFashion ? 'fashion' : 'video', mediaKind: 'video',
              url: permanent, thumbnail_url: imageUrl, title: `Vidéo — ${subjectName}`, content: { prompt: basePrompt, aspect },
              provider: 'xAI',
            });
            await logZGeneration({ ownerId: user.id, projectId: project.id, type: 'video', prompt: basePrompt, provider: 'xAI', output: { url: permanent }, status: 'done' });
            created += 1;
            setDoneCount(created);
          }
        }
      }

      await updateZProject(project.id, { status: 'ready' });
      setProgress('');
      setStep(5);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La génération a échoué.');
      setProgress('');
    } finally {
      setRunning(false);
    }
  };

  const next = () => { setError(null); setStep((s) => Math.min(4, s + 1)); };
  const prev = () => { setError(null); setStep((s) => Math.max(0, s - 1)); };
  const canNext = () => {
    if (step === 2) {
      if (source === 'product') return productId != null;
      if (source === 'service') return serviceId != null;
      return (resolveSubject().trim() !== '' || brief.trim() !== '');
    }
    return true;
  };

  // ── Écran de résultat ──
  if (step === 5) {
    return (
      <div className="p-4 md:p-6 max-w-2xl mx-auto text-center">
        <div className="w-16 h-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mx-auto mb-4">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950">Génération terminée</h2>
        <p className="text-sm text-foreground-500 mt-1">{doneCount} création(s) ajoutée(s). Retrouvez-les dans Mes créations.</p>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          <button type="button" onClick={() => { setStep(0); setProjectId(null); setDoneCount(0); }} className="px-4 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Nouvelle création</button>
          <Link to="/dashboard/zstudio/creations" className="px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-archive-2-line mr-1"></i>Voir mes créations
          </Link>
        </div>
        {projectId && (
          <p className="mt-5">
            <Link to="/dashboard/zstudio" className="text-xs text-foreground-400 hover:text-foreground-700 cursor-pointer">Retour à ZStudio</Link>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <Link to="/dashboard/zstudio" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-4 cursor-pointer">
        <i className="ri-arrow-left-line"></i>ZStudio
      </Link>

      <div className="mb-5">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-magic-line text-accent-500"></i>Créer
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Mode guidé : type → source → détails → style → format → génération.</p>
      </div>

      {/* Presets rapides (spec 67) */}
      <div className="mb-6">
        <p className="text-xs font-medium text-foreground-600 mb-2 flex items-center gap-1.5">
          <i className="ri-flashlight-line text-accent-500"></i>Démarrage rapide
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors cursor-pointer ${activePreset === p.key ? 'border-accent-400 bg-accent-50/60' : 'border-background-200/70 hover:border-primary-300'}`}
            >
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${activePreset === p.key ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
                <i className={p.icon}></i>
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold text-foreground-900 whitespace-nowrap">{p.label}</span>
                <span className="block text-[11px] text-foreground-500 whitespace-nowrap">{p.desc}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-6 flex-wrap">
        {STEP_LABELS.map((label, i) => (
          <div key={label} className="flex items-center gap-1">
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${i === step ? 'bg-primary-500 text-background-50' : i < step ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
              <i className={i < step ? 'ri-check-line' : 'ri-circle-fill text-[6px]'}></i>{label}
            </span>
            {i < STEP_LABELS.length - 1 && <i className="ri-arrow-right-s-line text-foreground-300"></i>}
          </div>
        ))}
      </div>

      {error && <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5">
        {/* Étape 1 — Type */}
        {step === 0 && (
          <div>
            <h3 className="text-sm font-bold text-foreground-900 mb-3">Que souhaitez-vous créer ?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {Z_KINDS.map((k) => (
                <button key={k.value} type="button" onClick={() => setKind(k.value)} className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors cursor-pointer ${kind === k.value ? 'border-primary-400 bg-primary-50/50' : 'border-background-200/70 hover:border-primary-300'}`}>
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-500 flex items-center justify-center flex-shrink-0"><i className={k.icon}></i></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-900">{k.label}</span>
                    <span className="block text-xs text-foreground-500">{k.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Étape 2 — Source */}
        {step === 1 && (
          <div>
            <h3 className="text-sm font-bold text-foreground-900 mb-3">D&apos;où partez-vous ?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {SOURCES.map((s) => (
                <button key={s.key} type="button" onClick={() => setSource(s.key)} className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors cursor-pointer ${source === s.key ? 'border-primary-400 bg-primary-50/50' : 'border-background-200/70 hover:border-primary-300'}`}>
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-500 flex items-center justify-center flex-shrink-0"><i className={s.icon}></i></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-900">{s.label}</span>
                    <span className="block text-xs text-foreground-500">{s.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Étape 3 — Détails */}
        {step === 2 && (
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-foreground-900">Détails</h3>
            {source === 'product' && (
              <div className="relative">
                <select value={productId ?? ''} onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : null)} className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer">
                  <option value="">Choisir un produit…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.price != null ? ` — ${p.price} ${p.currency || ''}` : ''}</option>)}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            )}
            {source === 'service' && (
              <div className="relative">
                <select value={serviceId ?? ''} onChange={(e) => setServiceId(e.target.value ? Number(e.target.value) : null)} className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer">
                  <option value="">Choisir un service…</option>
                  {services.map((s) => <option key={s.id} value={s.id}>{s.name}{s.price != null ? ` — ${s.price}` : ''}</option>)}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            )}
            {source !== 'product' && source !== 'service' && (
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Titre / sujet (ex : Robe traditionnelle festive)" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
            )}
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={3}
              placeholder={source === 'sketch' ? 'Décrivez le croquis : robe longue, manches bouffantes, broderie dorée, couleur ivoire…' : source === 'garment' ? 'Décrivez le vêtement : matière, coupe, motifs, détails…' : 'Décrivez votre idée (optionnel)'}
              className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
            />
            <p className="text-[11px] text-foreground-400 flex items-start gap-1.5"><i className="ri-information-line text-primary-500 mt-0.5"></i>Le design du produit/vêtement est conservé autant que possible ; l’IA n’invente jamais de prix ni de promotion.</p>
          </div>
        )}

        {/* Étape 4 — Style */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-foreground-900">Style &amp; intention</h3>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Style visuel</p>
              <div className="flex flex-wrap gap-1.5">{Z_STYLES.map((s) => <Toggle key={s} active={style === s} onClick={() => setStyle(s)}>{s}</Toggle>)}</div>
            </div>
            {isFashion && (
              <>
                <div>
                  <p className="text-xs font-medium text-foreground-600 mb-1.5">Environnement</p>
                  <div className="flex flex-wrap gap-1.5">{Z_ENVIRONMENTS.map((s) => <Toggle key={s} active={environment === s} onClick={() => setEnvironment(s)}>{s}</Toggle>)}</div>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-600 mb-1.5">Type de modèle</p>
                  <div className="flex flex-wrap gap-1.5">{Z_MODEL_TYPES.map((s) => <Toggle key={s} active={modelType === s} onClick={() => setModelType(s)}>{s}</Toggle>)}</div>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-600 mb-1.5">Pose</p>
                  <div className="flex flex-wrap gap-1.5">{Z_POSES.map((s) => <Toggle key={s} active={pose === s} onClick={() => setPose(s)}>{s}</Toggle>)}</div>
                </div>
              </>
            )}
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Objectif</p>
              <div className="flex flex-wrap gap-1.5">{Z_OBJECTIVES.map((s) => <Toggle key={s} active={objective === s} onClick={() => setObjective(s)}>{s}</Toggle>)}</div>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Saison / occasion</p>
              <div className="flex flex-wrap gap-1.5">{Z_SEASONS.map((s) => <Toggle key={s} active={season === s} onClick={() => setSeason(s)}>{s}</Toggle>)}</div>
            </div>
          </div>
        )}

        {/* Étape 5 — Format + génération */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-foreground-900">Format &amp; contenus</h3>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Format</p>
              <div className="flex flex-wrap gap-1.5">{Z_ASPECTS.map((a) => <Toggle key={a.value} active={aspect === a.value} onClick={() => setAspect(a.value)}>{a.label}</Toggle>)}</div>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Plateformes</p>
              <div className="flex flex-wrap gap-1.5">
                {Z_PLATFORMS.map((p) => (
                  <Toggle key={p} active={platforms.includes(p)} onClick={() => setPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p])}>{p}</Toggle>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">À générer</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {([['image', 'Image'], ['video', 'Vidéo'], ['text', 'Texte pub'], ['hashtags', 'Hashtags'], ['slogan', 'Slogans']] as const).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-background-200/70 cursor-pointer">
                    <input type="checkbox" checked={aspect3[key]} onChange={() => setAspect3((prev) => ({ ...prev, [key]: !prev[key] }))} className="accent-primary-500" />
                    <span className="text-xs text-foreground-700">{label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-foreground-400 mt-2">La vidéo IA peut prendre quelques minutes (génération asynchrone).</p>
            </div>

            {running && (
              <div className="bg-background-100 rounded-lg p-3">
                <div className="flex items-center gap-2 text-xs text-foreground-600">
                  <i className="ri-loader-4-line animate-spin text-primary-500"></i>{progress || 'Génération…'}
                </div>
                <p className="text-[11px] text-foreground-400 mt-1">{doneCount} création(s) déjà prête(s).</p>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between gap-2 mt-5 pt-4 border-t border-background-200/70">
          <button type="button" onClick={prev} disabled={step === 0 || running} className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 disabled:opacity-40 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>Retour
          </button>
          {step < 4 ? (
            <button type="button" onClick={next} disabled={!canNext()} className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
              Continuer<i className="ri-arrow-right-line"></i>
            </button>
          ) : (
            <button type="button" onClick={run} disabled={running} className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
              {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>}
              {running ? 'Génération…' : 'Générer'}
            </button>
          )}
        </div>
      </div>

      {/* Aperçu produit/service sélectionné */}
      {(source === 'product' || source === 'service') && (
        <div className="mt-4">
          {source === 'product' && productId && (() => { const p = products.find((x) => x.id === productId); const img = p ? firstProductImage(p.media) : null; return p ? (
            <div className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
              <div className="w-14 h-14 rounded-lg bg-background-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                {img ? <img src={img} alt={p.name} className="w-full h-full object-cover object-top" /> : <i className="ri-shopping-bag-3-line text-foreground-400"></i>}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground-900 truncate">{p.name}</p>
                <p className="text-xs text-foreground-500 truncate">{p.price != null ? `${p.price} ${p.currency || ''}` : 'Prix non renseigné'}</p>
              </div>
            </div>
          ) : null; })()}
          {source === 'service' && serviceId && (() => { const s = services.find((x) => x.id === serviceId); return s ? (
            <div className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
              <span className="w-14 h-14 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0"><i className="ri-service-line text-foreground-400"></i></span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground-900 truncate">{s.name}</p>
                <p className="text-xs text-foreground-500 truncate">{s.price != null ? `${s.price}` : 'Prix non renseigné'}</p>
              </div>
            </div>
          ) : null; })()}
        </div>
      )}
    </div>
  );
}